'use client';

import React, { useState, useMemo } from 'react';
import { Plus, Search, CheckCircle, AlertCircle } from 'lucide-react';
import {
  MOCK_GRADING_RECORDS, EGG_FARMS, EGG_HOUSES_BY_FARM, EGG_FLOCKS,
  EggGradingRecord
} from '../../components/eggProductionData';
import { useRole } from '@/lib/useRole';

function GradingForm({ onClose, onSave }: { onClose: () => void; onSave: (d: Partial<EggGradingRecord>) => void }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    farm: 'Idudi Poultry Farm',
    house: '',
    flockId: '',
    totalEggsReceived: '',
    gradeA: '',
    gradeB: '',
    smallEggs: '',
    mediumEggs: '',
    largeEggs: '',
    extraLargeEggs: '',
    hatchingEggs: '',
    rejected: '',
    gradedBy: 'Sarah Namukasa',
    notes: '',
  });
  const [error, setError] = useState('');

  const set = (k: string, v: string) => setForm(prev => ({ ...prev, [k]: v }));
  const n = (v: string) => parseInt(v, 10) || 0;
  const total = n(form.gradeA) + n(form.gradeB) + n(form.smallEggs) + n(form.mediumEggs) + n(form.largeEggs) + n(form.extraLargeEggs) + n(form.hatchingEggs) + n(form.rejected);
  const received = n(form.totalEggsReceived);
  const excess = total > received && received > 0;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.flockId) { setError('Please select a flock.'); return; }
    if (received === 0) { setError('Total eggs received must be > 0.'); return; }
    if (excess) { setError(`Graded total (${total}) exceeds received (${received}).`); return; }
    const flock = EGG_FLOCKS.find(f => f.id === form.flockId);
    onSave({
      ...form,
      flockNumber: flock?.flockNumber ?? '',
      totalEggsReceived: received,
      gradeA: n(form.gradeA), gradeB: n(form.gradeB),
      smallEggs: n(form.smallEggs), mediumEggs: n(form.mediumEggs),
      largeEggs: n(form.largeEggs), extraLargeEggs: n(form.extraLargeEggs),
      hatchingEggs: n(form.hatchingEggs), rejected: n(form.rejected),
    });
  }

  const houses = EGG_HOUSES_BY_FARM[form.farm] ?? [];
  const flocks = EGG_FLOCKS.filter(f => f.farm === form.farm);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">New Grading Record</h2>
          <p className="text-xs text-gray-400 mt-0.5">Grade eggs by size and quality</p>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-lg">
              <AlertCircle size={14} />{error}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Date</label>
              <input type="date" value={form.date} onChange={e => set('date', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Farm</label>
              <select value={form.farm} onChange={e => set('farm', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                {EGG_FARMS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">House</label>
              <select value={form.house} onChange={e => set('house', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value="">Select House</option>
                {houses.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Flock *</label>
              <select value={form.flockId} onChange={e => set('flockId', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value="">Select Flock</option>
                {flocks.map(f => <option key={f.id} value={f.id}>{f.flockNumber}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Total Eggs Received *</label>
              <input type="number" value={form.totalEggsReceived} onChange={e => set('totalEggsReceived', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="0" min="0" />
            </div>
          </div>
          <div>
            <p className="text-xs font-bold text-gray-700 mb-2">Grading Breakdown</p>
            {excess && <p className="text-xs text-red-500 mb-2">Graded total ({total}) exceeds received ({received})</p>}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { k: 'gradeA', l: 'Grade A', c: 'text-green-600' },
                { k: 'gradeB', l: 'Grade B', c: 'text-blue-600' },
                { k: 'smallEggs', l: 'Small', c: 'text-gray-600' },
                { k: 'mediumEggs', l: 'Medium', c: 'text-gray-600' },
                { k: 'largeEggs', l: 'Large', c: 'text-gray-600' },
                { k: 'extraLargeEggs', l: 'Extra Large', c: 'text-gray-600' },
                { k: 'hatchingEggs', l: 'Hatching', c: 'text-purple-600' },
                { k: 'rejected', l: 'Rejected', c: 'text-red-500' },
              ].map(({ k, l, c }) => (
                <div key={k}>
                  <label className={`block text-xs font-semibold mb-1 ${c}`}>{l}</label>
                  <input type="number" value={(form as Record<string, string>)[k]} onChange={e => set(k, e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="0" min="0" />
                </div>
              ))}
            </div>
            <div className="mt-3 bg-gray-50 rounded-lg px-3 py-2 flex items-center gap-3">
              <span className="text-xs text-gray-500">Graded Total:</span>
              <span className={`text-sm font-bold ${excess ? 'text-red-600' : 'text-gray-900'}`}>{total.toLocaleString()}</span>
              <span className="text-xs text-gray-400">/ {received.toLocaleString()} received</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Graded By</label>
              <input type="text" value={form.gradedBy} onChange={e => set('gradedBy', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
              <input type="text" value={form.notes} onChange={e => set('notes', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="Optional notes" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" className="flex-1 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors">Save Grading Record</button>
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function EggGradingContent() {
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [records, setRecords] = useState<EggGradingRecord[]>(MOCK_GRADING_RECORDS);
  const [toast, setToast] = useState<string | null>(null);
  const { can } = useRole();

  const filtered = useMemo(() =>
    records.filter(r =>
      !search ||
      r.gradingNumber.toLowerCase().includes(search.toLowerCase()) ||
      r.flockNumber.toLowerCase().includes(search.toLowerCase())
    ), [records, search]);

  function handleSave(data: Partial<EggGradingRecord>) {
    const newRec: EggGradingRecord = {
      id: `gr-${Date.now()}`,
      gradingNumber: `GR-2026-${String(records.length + 46).padStart(3, '0')}`,
      date: data.date ?? new Date().toISOString().split('T')[0],
      farm: data.farm ?? '',
      house: data.house ?? '',
      flockId: data.flockId ?? '',
      flockNumber: data.flockNumber ?? '',
      totalEggsReceived: data.totalEggsReceived ?? 0,
      gradeA: data.gradeA ?? 0,
      gradeB: data.gradeB ?? 0,
      smallEggs: data.smallEggs ?? 0,
      mediumEggs: data.mediumEggs ?? 0,
      largeEggs: data.largeEggs ?? 0,
      extraLargeEggs: data.extraLargeEggs ?? 0,
      hatchingEggs: data.hatchingEggs ?? 0,
      rejected: data.rejected ?? 0,
      gradedBy: data.gradedBy ?? '',
      notes: data.notes,
      createdAt: new Date().toISOString(),
    };
    setRecords(prev => [newRec, ...prev]);
    setShowForm(false);
    setToast(`Grading record ${newRec.gradingNumber} saved.`);
    setTimeout(() => setToast(null), 3000);
  }

  return (
    <div className="p-6">
      {toast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-3 bg-gray-900 text-white text-sm px-4 py-3 rounded-xl shadow-xl max-w-sm">
          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />{toast}
        </div>
      )}
      {showForm && <GradingForm onClose={() => setShowForm(false)} onSave={handleSave} />}

      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Egg Grading</h1>
          <p className="text-sm text-gray-500 mt-1">Grade eggs by size, weight, quality and intended use.</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className={`flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors shadow-sm ${!can('egg_grading.manage') ? 'hidden' : ''}`}
        >
          <Plus size={15} />New Grading Record
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Graded Today', value: '2,890', sub: 'Layer House 01', color: 'bg-green-100 text-green-600' },
          { label: 'Grade A', value: '2,420', sub: '83.7% of total', color: 'bg-blue-100 text-blue-600' },
          { label: 'Grade B', value: '330', sub: '11.4% of total', color: 'bg-amber-100 text-amber-600' },
          { label: 'Rejected', value: '60', sub: '2.1% of total', color: 'bg-red-100 text-red-600' },
        ].map(c => (
          <div key={c.label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${c.color.split(' ')[0]}`}>
              <CheckCircle size={16} className={c.color.split(' ')[1]} />
            </div>
            <p className="text-xs text-gray-500 font-medium">{c.label}</p>
            <p className="text-xl font-bold text-gray-900 mt-0.5">{c.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{c.sub}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-5">
        <div className="relative max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search grading records..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {['Grading #', 'Date', 'Flock', 'House', 'Received', 'Grade A', 'Grade B', 'Small', 'Medium', 'Large', 'XL', 'Hatching', 'Rejected', 'Graded By'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={14} className="px-4 py-10 text-center text-sm text-gray-400">No grading records found.</td></tr>
              ) : filtered.map((rec, i) => (
                <tr key={rec.id} className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                  <td className="px-4 py-3 text-xs font-semibold text-green-700">{rec.gradingNumber}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{rec.date}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{rec.flockNumber}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{rec.house}</td>
                  <td className="px-4 py-3 text-xs font-semibold text-gray-900">{rec.totalEggsReceived.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs text-green-600 font-medium">{rec.gradeA.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs text-blue-600">{rec.gradeB.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{rec.smallEggs}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{rec.mediumEggs.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{rec.largeEggs.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{rec.extraLargeEggs}</td>
                  <td className="px-4 py-3 text-xs text-purple-600">{rec.hatchingEggs}</td>
                  <td className="px-4 py-3 text-xs text-red-500">{rec.rejected}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{rec.gradedBy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
